import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { useNotify } from 'react-admin'
import { Controller, useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router'
import { Card, Wrap } from 'shared/components/Base/Card'
import { Button } from 'shared/components/Button/Button'
import { Checkbox } from 'shared/components/Checkbox/Checkbox'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { Dropdown, DropdownOption } from 'shared/components/Dropdown/Dropdown'
import { PhoneInput } from 'shared/components/MaskedInput/PhoneInput'
import { Text } from 'shared/components/Text/Text'
import { TextInput } from 'shared/components/TextInput/TextInput'
import { convertMobileForAPI } from 'shared/helpers/formatters'
import { toTitleCase } from 'shared/helpers/utils'
import { sizes } from 'shared/theme'
import { bgGray } from 'shared/theme/colors'
import { boolean, object, string } from 'yup'

import { getBrokerCarrierRelationship, updateBrokerCarrierRelationship } from 'api/brokers'
import {
  BrokerCarrierRelationship,
  BrokerCarrierRelationshipStatus,
  PaymentMethod,
  SubmissionMethod,
} from 'models/broker'
import { getBrokerTransitionProps, REASONS_REQUIRED_BROKER_STATUSES } from './BrokerStatusTransitions'

interface BrokerCarrierRelationshipEditProps {
  carrier_relationship: BrokerCarrierRelationship
}

export interface BrokerCarrierRelationshipFormPayload {
  status: BrokerCarrierRelationshipStatus
  is_carrier_packet_complete: boolean
  is_invoicing_submission_automated: boolean
  payment_method: PaymentMethod
  booking_contact_phone: string
  booking_contact_email: string
  support_main_phone: string
  support_main_email: string
  use_support_email_for_tracking: boolean
  submission_requirement_notes: string
}

const CarrierRelationshipSchema = object().shape({
  booking_contact_email: string().email(),
  booking_contact_phone: string(),
  status: string().required(),
  support_main_email: string().email(),
  support_main_phone: string(),
  is_carrier_packet_complete: boolean(),
  is_invoicing_submission_automated: boolean(),
  payment_method: string().required(),
  use_support_email_for_tracking: boolean(),
  submission_requirement_notes: string(),
})

function getBrokerCarrierRelationshipForm(
  carrier_relationship: BrokerCarrierRelationship
): BrokerCarrierRelationshipFormPayload {
  return {
    status: carrier_relationship.status,
    is_carrier_packet_complete: carrier_relationship.is_carrier_packet_complete,
    is_invoicing_submission_automated: carrier_relationship.is_invoicing_submission_automated,
    payment_method: carrier_relationship.payment_method,
    booking_contact_phone: carrier_relationship.booking_contact_phone,
    booking_contact_email: carrier_relationship.booking_contact_email,
    support_main_phone: carrier_relationship.support_main_phone,
    support_main_email: carrier_relationship.support_main_email,
    use_support_email_for_tracking: carrier_relationship.use_support_email_for_tracking,
    submission_requirement_notes: carrier_relationship.submission_requirement_notes,
  }
}

export function BrokerCarrierRelationshipEdit() {
  const { id } = useParams<{ id: string }>()
  const [brokerCarrierRelationship, setBrokerCarrierRelationship] =
    useState<BrokerCarrierRelationship | null>()
  useEffect(() => {
    getBrokerCarrierRelationship(id)
      .then((res) => {
        setBrokerCarrierRelationship(res.data)
      })
      .catch((err) => {
        console.error(err)
      })
  }, [id])

  if (!brokerCarrierRelationship) {
    return null
  }

  return <BrokerCarrierRelationshipForm carrier_relationship={brokerCarrierRelationship} />
}

function BrokerCarrierRelationshipForm({ carrier_relationship }: BrokerCarrierRelationshipEditProps) {
  const [isLoading, setIsLoading] = useState(false)
  const history = useHistory()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const notify = useNotify()
  const { handleSubmit, control, setError, watch } = useForm<BrokerCarrierRelationship>({
    defaultValues: getBrokerCarrierRelationshipForm(carrier_relationship),
    mode: 'onSubmit',
    resolver: yupResolver(CarrierRelationshipSchema),
  })
  const watchStatus = watch('status')
  const watchStatusReason = watch('status_reason')
  async function onSubmit(formFields: BrokerCarrierRelationshipFormPayload) {
    if (formFields.is_carrier_packet_complete && !carrier_relationship.carrier_agreement) {
      setError('is_carrier_packet_complete', {
        type: 'required',
        message: 'Carrier agreement must be uploaded',
      })
      return
    }
    formFields.support_main_phone = convertMobileForAPI(formFields.support_main_phone)
    formFields.booking_contact_phone = convertMobileForAPI(formFields.booking_contact_phone)
    // TODO: validate phone numbers are valid or empty using regex

    setIsLoading(true)
    try {
      await updateBrokerCarrierRelationship(carrier_relationship.id, formFields)
      history.push(`/brokers/${carrier_relationship.broker?.id}/show`)
      notify('Broker carrier relationship updated', { type: 'success' })
    } catch (err: any) {
      console.error(err)
      notify(
        `Failed to update broker carrier relationship: ${err.response?.data?.errors
          ?.map((error: any) => error.detail)
          .join(',')}`,
        { type: 'error' }
      )
      for (const error of err.response?.data?.errors) {
        setError(error.attr as keyof BrokerCarrierRelationship, {
          message: error.detail as string,
          type: 'notMatch',
        })
      }
    }
    setIsLoading(false)
  }
  const handleButtonClick = () => {
    if (
      carrier_relationship.status !== watchStatus &&
      REASONS_REQUIRED_BROKER_STATUSES.includes(watchStatus)
    ) {
      setIsDialogOpen(true)
    } else {
      handleSubmit(onSubmit)()
    }
  }
  const handleDialogSubmit = () => {
    handleSubmit(onSubmit)()
    setIsDialogOpen(false)
  }
  const brokerStatusTransitions = getBrokerTransitionProps(carrier_relationship.status) || []
  const statusOptions: DropdownOption<string>[] = brokerStatusTransitions.map((value) => {
    return {
      label: value.name,
      value: value.id,
    }
  })

  const paymentMethodOptions: DropdownOption<PaymentMethod>[] = [
    {
      label: 'ACH',
      value: PaymentMethod.ACH,
    },
    {
      label: 'Check',
      value: PaymentMethod.CHECK,
    },
  ]
  return (
    <Card align="left">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Text variant="header2" align="left" mb={3}>
          Edit {carrier_relationship.carrier?.name} - {carrier_relationship?.broker?.name} details
        </Text>
        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <Dropdown
              {...field}
              staticLabel="Carrier-broker relationship"
              options={statusOptions}
              label="Status"
              containerMaxWidth="25%"
              backgroundColor={bgGray}
            />
          )}
        />
        <Controller
          name="is_carrier_packet_complete"
          control={control}
          render={({ field, fieldState }) => (
            <Checkbox {...field} error={fieldState.error?.message} label="Is carrier packet complete?" />
          )}
        />
        {carrier_relationship.broker?.bol_submission_method === SubmissionMethod.EMAIL && (
          <Controller
            name="is_invoicing_submission_automated"
            control={control}
            render={({ field }) => <Checkbox {...field} label="Auto-email invoice?" />}
          />
        )}
        <Controller
          name="payment_method"
          control={control}
          render={({ field }) => (
            <Dropdown
              {...field}
              staticLabel="Payment method"
              options={paymentMethodOptions}
              label="Payment method"
              backgroundColor={bgGray}
            />
          )}
        />
        <Controller
          name="booking_contact_phone"
          control={control}
          render={({ field, fieldState }) => (
            <PhoneInput
              {...field}
              label="Booking phone"
              type="tel"
              error={fieldState.error?.message}
              backgroundColor={bgGray}
            />
          )}
        />
        <Controller
          name="booking_contact_email"
          control={control}
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              label="Booking email"
              type="email"
              inputMode="email"
              backgroundColor={bgGray}
              error={fieldState.error?.message}
            />
          )}
          rules={{
            pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email' },
          }}
        />
        <Controller
          name="support_main_phone"
          control={control}
          render={({ field, fieldState }) => (
            <PhoneInput
              {...field}
              label="Support main phone"
              type="tel"
              backgroundColor={bgGray}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="support_main_email"
          control={control}
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              label="Support main email"
              type="email"
              inputMode="email"
              backgroundColor={bgGray}
              error={fieldState.error?.message}
            />
          )}
          rules={{
            pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email' },
          }}
        />
        <Controller
          name="use_support_email_for_tracking"
          control={control}
          render={({ field }) => <Checkbox {...field} label="Use support email for tracking" />}
        />
        <Controller
          name="submission_requirement_notes"
          control={control}
          render={({ field }) => (
            <TextInput {...field} label="Submission requirement notes" type="text" backgroundColor={bgGray} />
          )}
        />
        <Button type="button" size={sizes.large} onClick={handleButtonClick}>
          Save
        </Button>
        <SimpleDialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
          <Wrap maxWidth="100%" width="600px">
            <Text variant="header2" align="left" mb={3}>
              Changing the broker status to "{toTitleCase(watchStatus).replace('_', ' ')}"
            </Text>
            <Controller
              name="status_reason"
              control={control}
              render={({ field }) => (
                <TextInput {...field} label="Status reason" type="text" backgroundColor={bgGray} />
              )}
              rules={{
                required: 'Reason is required',
              }}
            />
            <Button
              type="button"
              onClick={handleDialogSubmit}
              size={sizes.large}
              margin="0 auto"
              isLoading={isLoading}
              isDisabled={!watchStatusReason}
            >
              Save
            </Button>
          </Wrap>
        </SimpleDialog>
      </form>
    </Card>
  )
}
